// DTOs de C7 — análisis de personal (coordinador).
//
// Dos secciones: líderes (foco en producción) y supervisores (foco en
// gestión: validaciones, invalidaciones, delegaciones).
//
// Endpoint:
//   GET /api/coordinador/analisis-personal?desde&hasta&planta_id&area_id&linea_id&turno_id&supervisor_user_id
import { z } from 'zod';
// =============================================================================
// Filtros
// =============================================================================
export const analisisPersonalQuerySchema = z.object({
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
    planta_id: z.string().uuid().optional(),
    area_id: z.string().uuid().optional(),
    // Filtro pedido por Robotpack (2026-08): acotar a UNA línea (antes solo área).
    linea_id: z.string().uuid().optional(),
    // Filtros pedidos por Robotpack (reunión 2026): ver productividad de personal
    // acotada a un turno y/o al equipo de un supervisor específico.
    turno_id: z.string().uuid().optional(),
    supervisor_user_id: z.string().uuid().optional(),
});
// =============================================================================
// Recursos
// =============================================================================
const plantaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
});
const areaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
});
// Opción del dropdown de línea: lleva area_id para encadenar con el filtro de
// área (elegida un área, solo se ofrecen sus líneas).
const lineaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
    area_id: z.string().uuid(),
    planta_id: z.string().uuid(),
});
const turnoResumenSchema = z.object({
    id: z.string().uuid(),
    numero: z.number().int(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
});
// Opción del dropdown de supervisor: solo los programados en el rango/turno.
const supervisorOpcionSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
});
// =============================================================================
// Métricas del líder
// =============================================================================
export const personalLiderSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    email: z.string().email(),
    /** Área de la línea del líder (un líder vive en UNA línea). null si no tiene línea asignada. */
    area_nombre: z.string().nullable(),
    /** Nombre legible de la línea del líder (ej. "Vertical 1"). null si no tiene línea asignada. */
    linea_nombre: z.string().nullable(),
    /** Código de la línea del líder (ej. "V1"). null si no tiene línea asignada. */
    linea_codigo: z.string().nullable(),
    /** Total unidades procesadas (SOLO registros estado='valido') en el rango. */
    cantidad_real: z.number().min(0),
    /**
     * null SIEMPRE (V4): el programa vive por (área, sku) y no es atribuible a un
     * líder — el join por línea anterior daba 0 sistemático. El % del líder se
     * mide solo vs capacidad (cumplimiento_meta_pct). Campo conservado por
     * compatibilidad de contrato.
     */
    cantidad_programa: z.number().min(0).nullable(),
    /** null SIEMPRE (V4): sin programa atribuible no hay % vs programa. */
    cumplimiento_pct: z.number().min(0).nullable(),
    /**
     * Capacidad acumulada esperada de sus bloques válidos:
     * SUM(capacidad_estandar_unidades_hora * bloques) por (sku,línea).
     */
    esperado_capacidad: z.number().min(0),
    /** Cumplimiento vs capacidad: producido / esperado_capacidad * 100 (null si esperado=0). */
    cumplimiento_meta_pct: z.number().min(0).nullable(),
    /** Cada bloque = 1 hora de captura. */
    bloques_capturados: z.number().int().min(0),
    /** Días distintos con al menos 1 captura. */
    dias_capturados: z.number().int().min(0),
});
// =============================================================================
// Métricas del supervisor
// =============================================================================
export const personalSupervisorSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    email: z.string().email(),
    /** Días programados con turno en el rango. */
    dias_programados: z.number().int().min(0),
    /** Turnos distintos programados (fecha, turno_id) en programacion_supervisores. */
    turnos: z.number().int().min(0),
    /** Unidades producidas atribuidas al supervisor (supervisor_user_id, solo estado='valido'). */
    producido: z.number().min(0),
    /** Capacidad acumulada esperada de los bloques atribuidos al supervisor. */
    esperado_capacidad: z.number().min(0),
    /** Cumplimiento vs capacidad: producido / esperado_capacidad * 100 (null si esperado=0). */
    cumplimiento_meta_pct: z.number().min(0).nullable(),
    /** Validaciones realizadas (registros donde validado_por_user_id = supervisor). */
    validaciones: z.number().int().min(0),
    /** Invalidaciones realizadas. */
    invalidaciones: z.number().int().min(0),
    /** Capturas por delegación (capturado_por_user_id = supervisor AND es != lider_asignado_user_id). */
    capturas_delegadas: z.number().int().min(0),
    /**
     * Paros **CONFIRMADOS** por este supervisor (`confirmado_sup_por_user_id`,
     * mig 079). El nombre del campo quedó por historia: antes contaba
     * `cerrado_por_supervisor=TRUE`, un flujo que nunca se implementó (columna
     * siempre FALSE ⇒ métrica 0 en silencio). Cerrar es del líder o del técnico.
     */
    paros_cerrados: z.number().int().min(0),
    /** Minutos promedio entre captura (created_at) y validación (validado_at) de lo que validó. null si no validó. */
    oportunidad_min: z.number().nullable(),
    /** Total acciones = validaciones + invalidaciones + capturas_delegadas. */
    total_acciones: z.number().int().min(0),
    /**
     * Bloques SIN validar de los turnos que cubre (misma atribución que
     * `producido`). Retienen la facturación de su OP (DN4). Aditivo 2026-09-08.
     */
    pendientes_validacion: z.number().int().min(0).optional(),
    /** Paquetes de esos bloques. */
    pendientes_paq: z.number().min(0).optional(),
});
// =============================================================================
// Totales agregados
// =============================================================================
export const analisisPersonalTotalesSchema = z.object({
    lideres_evaluados: z.number().int().min(0),
    supervisores_evaluados: z.number().int().min(0),
    cantidad_real_total: z.number().min(0),
    /** null SIEMPRE (V4): ver personalLiderSchema.cantidad_programa. */
    cantidad_programa_total: z.number().min(0).nullable(),
    cumplimiento_promedio_pct: z.number().min(0).nullable(),
    /** Capacidad esperada acumulada de todos los líderes. */
    esperado_capacidad_total: z.number().min(0),
    /** Cumplimiento promedio vs capacidad: cantidad_real_total / esperado_capacidad_total * 100. */
    cumplimiento_meta_promedio_pct: z.number().min(0).nullable(),
    validaciones_total: z.number().int().min(0),
    invalidaciones_total: z.number().int().min(0),
    capturas_delegadas_total: z.number().int().min(0),
    /** Bloques sin validar atribuidos a los supervisores listados (DN4). */
    pendientes_validacion_total: z.number().int().min(0).optional(),
    pendientes_paq_total: z.number().min(0).optional(),
});
// =============================================================================
// Response GET /api/coordinador/analisis-personal
// =============================================================================
export const coordinadorAnalisisPersonalSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    scope: z.object({
        plantas_asignadas: z.array(plantaResumenSchema),
    }),
    filtros: z.object({
        desde: z.string().date(),
        hasta: z.string().date(),
        planta_id: z.string().uuid().nullable(),
        area_id: z.string().uuid().nullable(),
        linea_id: z.string().uuid().nullable(),
        turno_id: z.string().uuid().nullable(),
        supervisor_user_id: z.string().uuid().nullable(),
    }),
    recursos: z.object({
        plantas: z.array(plantaResumenSchema),
        areas: z.array(areaResumenSchema),
        lineas: z.array(lineaResumenSchema),
        turnos: z.array(turnoResumenSchema),
        supervisores: z.array(supervisorOpcionSchema),
    }),
    totales: analisisPersonalTotalesSchema,
    /** Ordenado por cantidad_real DESC. */
    lideres: z.array(personalLiderSchema),
    /** Ordenado por total_acciones DESC. */
    supervisores: z.array(personalSupervisorSchema),
});
//# sourceMappingURL=coordinador_analisis_personal.js.map